import React, { useState, useEffect } from 'react';
import { Save, Server, ShieldCheck, Mail, HardDrive, Filter, Activity, Zap, Lock, Globe, Database, Settings2 } from 'lucide-react';
import adminService from '../../services/adminService';

const ServerSettings = () => {
    const [settings, setSettings] = useState({
        smtpHost: '', smtpPort: 587,
        imapHost: '', imapPort: 993,
        fromEmail: '', maxAttachmentSize: 25,
        dailySendingLimit: 500,
        retryCount: 3,
        timeout: 30
    });
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const data = await adminService.getSettings();
            if (data) setSettings(data);
        } catch (error) {
            console.error('Failed to fetch settings', error);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await adminService.updateSettings(settings);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            console.error('Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-8 animate-fade-in max-w-6xl">
            <header className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4f46e5]/30 decoration-4 underline-offset-8 uppercase tracking-wider">MTA Configuration</h1>
                    <p className="text-slate-500 mt-2 font-medium">Global policy engine and SMTP/IMAP protocol parameters.</p>
                </div>
                <div className="flex items-center text-emerald-500 bg-emerald-500/10 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border border-emerald-500/20">
                    <Activity size={14} className="mr-2" />
                    Engine Synchronized
                </div>
            </header>

            <form onSubmit={handleSave} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* SMTP Configuration */}
                    <div className="enterprise-card p-8 space-y-6 group">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                                <div className="p-3 rounded-2xl bg-blue-500 text-white shadow-xl shadow-blue-500/20 group-hover:rotate-12 transition-transform">
                                    <Zap size={24} />
                                </div>
                                <div>
                                    <h3 className="font-extrabold tracking-tight uppercase text-sm">Egress Relay (SMTP)</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Outbound Transmission Vector</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 pt-4">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Host Endpoint</label>
                                <div className="relative">
                                    <Globe size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="smtp.enterprise.com"
                                        className="premium-input pl-12"
                                        value={settings.smtpHost}
                                        onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Access Port</label>
                                <div className="relative">
                                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="number"
                                        className="premium-input pl-12 font-mono"
                                        value={settings.smtpPort}
                                        onChange={(e) => setSettings({ ...settings, smtpPort: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* IMAP Configuration */}
                    <div className="enterprise-card p-8 space-y-6 group">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                                <div className="p-3 rounded-2xl bg-purple-500 text-white shadow-xl shadow-purple-500/20 group-hover:rotate-12 transition-transform">
                                    <Database size={24} />
                                </div>
                                <div>
                                    <h3 className="font-extrabold tracking-tight uppercase text-sm">Ingress Sync (IMAP)</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Inbound Synchronization Protocol</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 pt-4">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Host Endpoint</label>
                                <div className="relative">
                                    <Globe size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="imap.enterprise.com"
                                        className="premium-input pl-12"
                                        value={settings.imapHost}
                                        onChange={(e) => setSettings({ ...settings, imapHost: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Access Port</label>
                                <div className="relative">
                                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="number"
                                        className="premium-input pl-12 font-mono"
                                        value={settings.imapPort}
                                        onChange={(e) => setSettings({ ...settings, imapPort: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Operational Limits */}
                    <div className="enterprise-card p-8 space-y-6 group md:col-span-2">
                        <div className="flex items-center space-x-3 mb-4">
                            <div className="p-3 rounded-2xl bg-emerald-500 text-white shadow-xl shadow-emerald-500/20 group-hover:rotate-12 transition-transform">
                                <Settings2 size={24} />
                            </div>
                            <div>
                                <h3 className="font-extrabold tracking-tight uppercase text-sm">Transmission Policies & Resource Bounds</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">MTA Execution Constraints</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-4">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Official Relay Identity</label>
                                    <div className="relative">
                                        <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="email"
                                            className="premium-input pl-12 font-bold"
                                            value={settings.fromEmail}
                                            onChange={(e) => setSettings({ ...settings, fromEmail: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Temporal Timeout (sec)</label>
                                    <input
                                        type="number"
                                        className="premium-input font-bold"
                                        value={settings.timeout}
                                        onChange={(e) => setSettings({ ...settings, timeout: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Max Payload Size (MB)</label>
                                    <input
                                        type="number"
                                        className="premium-input font-bold"
                                        value={settings.maxAttachmentSize}
                                        onChange={(e) => setSettings({ ...settings, maxAttachmentSize: parseInt(e.target.value) })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Error Recovery Retries</label>
                                    <input
                                        type="number"
                                        className="premium-input font-bold"
                                        value={settings.retryCount}
                                        onChange={(e) => setSettings({ ...settings, retryCount: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1">Daily Cap per Identity</label>
                                    <input
                                        type="number"
                                        className="premium-input font-bold border-blue-500/50 text-blue-600"
                                        value={settings.dailySendingLimit}
                                        onChange={(e) => setSettings({ ...settings, dailySendingLimit: parseInt(e.target.value) })}
                                    />
                                </div>
                                <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-[rgb(var(--border))] flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                                            <HardDrive size={20} />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">System Storage</span>
                                            <span className="text-xs font-extrabold uppercase tracking-widest">Optimized</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-xs font-extrabold text-blue-500 tracking-tighter">Active Pool</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-6 pt-4">
                    <button
                        type="submit"
                        disabled={saving}
                        className="premium-button-primary px-10 flex items-center group shadow-blue-500/40"
                    >
                        {saving ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                                <span>Persisting...</span>
                            </>
                        ) : (
                            <>
                                <Save size={20} className="mr-3 group-hover:scale-110 transition-transform" />
                                <span>Commit Transitions</span>
                            </>
                        )}
                    </button>
                    {success && <div className="flex items-center text-emerald-500 font-extrabold text-xs uppercase tracking-[0.2em] animate-fade-in">
                        <ShieldCheck size={16} className="mr-2" />
                        System variables updated successfully
                    </div>}
                </div>
            </form>
        </div>
    );
};

export default ServerSettings;
